/**
 * View-only evaluation page: 5 gold + 5 synthetic cases, 4 (gold) or 3 (synthetic) prompt variants,
 * turn selector, and clear display of context, clinician line, expected response, and model×variant outputs.
 */

import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../services/api';
import { BeakerIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { SparklesIcon } from '@heroicons/react/24/outline';
import type { EvalCase, EvalTurn } from '../types';

const MEDGEMMA = 'MedGemma-4B Patient LoRA v4';
const QWEN = 'Qwen2.5-7B Patient LoRA v4';

interface LogicalCase {
  sourceType: 'gold' | 'synthetic';
  sampleId: number;
  title: string;
}

function getModelColor(modelName: string): { bg: string; text: string; border: string } {
  if (modelName.includes('MedGemma')) return { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' };
  if (modelName.includes('Qwen')) return { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' };
  return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' };
}

/** Friendly label for exp1 variant strings */
function variantShortLabel(v: string): string {
  if (v.startsWith('variant_1')) return 'Variant 1';
  if (v.startsWith('variant_2')) return 'Variant 2';
  if (v.startsWith('variant_3')) return 'Variant 3';
  if (v.startsWith('variant_4')) return 'Variant 4';
  if (v.startsWith('synthetic_variant_1')) return 'Variant 1';
  if (v.startsWith('synthetic_variant_2')) return 'Variant 2';
  if (v.startsWith('synthetic_variant_3')) return 'Variant 3';
  return v;
}

/** Plain-English description so reviewers know what each variant is (no jargon) */
function variantDescription(v: string, sourceType: 'gold' | 'synthetic'): string {
  if (sourceType === 'gold') {
    if (v.startsWith('variant_1')) return 'The family\'s own details (background, communication style, trust in healthcare, coping style, etc.) are all set to "unknown". The child\'s name, age and diagnosis are still given.';
    if (v.startsWith('variant_2')) return 'Random categories are filled in (e.g. communication style, literacy, trust in healthcare, coping style, conversation context). The family\'s background stays "unknown" and the opening statement is the real one from the case.';
    if (v.startsWith('variant_3')) return 'Same as Variant 2, but the family\'s opening statement is removed and set to "not specified" so the model does not see what the family said first.';
    if (v.startsWith('variant_4')) return 'Different medical trust and coping style (e.g. low or mixed trust, avoidant or meaning-focused coping) to see how the model adapts when trust or coping vary.';
  } else {
    if (v.startsWith('synthetic_variant_1')) return 'All details set to "unknown" — the model gets no specific information about the family or the child.';
    if (v.startsWith('synthetic_variant_2')) return 'Full information — the full, original-style system prompt with complete patient and family details (as in the original synthetic case).';
    if (v.startsWith('synthetic_variant_3')) return 'Random categories filled in (e.g. communication style, literacy) but not the full personal story.';
  }
  return '';
}

/** Sort key for variant order (1,2,3,4 or 1,2,3) */
function variantOrder(v: string): number {
  const m = v.match(/variant_(\d+)/);
  return m ? parseInt(m[1], 10) : 99;
}

/** Whether this case is from exp1 JSONL (gold/synthetic with variant_* or synthetic_variant_*) */
function isExp1Case(c: EvalCase): boolean {
  const p = c.promptVariant ?? '';
  return p.startsWith('variant_') || p.startsWith('synthetic_variant_');
}

/** Line-based diff: returns aligned rows for left (baseline) and right (other) with type for styling */
function lineDiff(baseline: string, other: string): { left: string; right: string; type: 'common' | 'leftOnly' | 'rightOnly' }[] {
  const leftLines = baseline.split('\n');
  const rightLines = other.split('\n');
  const result: { left: string; right: string; type: 'common' | 'leftOnly' | 'rightOnly' }[] = [];
  let i = 0;
  let j = 0;
  while (i < leftLines.length || j < rightLines.length) {
    if (i < leftLines.length && j < rightLines.length && leftLines[i] === rightLines[j]) {
      result.push({ left: leftLines[i], right: rightLines[j], type: 'common' });
      i++;
      j++;
    } else if (i < leftLines.length && j < rightLines.length) {
      // Try to find next match for left in right, or right in left
      const nextLeftInRight = rightLines.indexOf(leftLines[i], j);
      const nextRightInLeft = leftLines.indexOf(rightLines[j], i);
      if (nextLeftInRight === j && nextRightInLeft === i) {
        result.push({ left: leftLines[i], right: '', type: 'leftOnly' });
        result.push({ left: '', right: rightLines[j], type: 'rightOnly' });
        i++;
        j++;
      } else if (nextRightInLeft >= 0 && (nextLeftInRight < 0 || nextRightInLeft - i <= nextLeftInRight - j)) {
        while (i < nextRightInLeft) {
          result.push({ left: leftLines[i], right: '', type: 'leftOnly' });
          i++;
        }
        result.push({ left: leftLines[i], right: rightLines[j], type: 'common' });
        i++;
        j++;
      } else if (nextLeftInRight >= 0) {
        while (j < nextLeftInRight) {
          result.push({ left: '', right: rightLines[j], type: 'rightOnly' });
          j++;
        }
        result.push({ left: leftLines[i], right: rightLines[j], type: 'common' });
        i++;
        j++;
      } else {
        result.push({ left: leftLines[i], right: '', type: 'leftOnly' });
        result.push({ left: '', right: rightLines[j], type: 'rightOnly' });
        i++;
        j++;
      }
    } else if (i < leftLines.length) {
      result.push({ left: leftLines[i], right: '', type: 'leftOnly' });
      i++;
    } else {
      result.push({ left: '', right: rightLines[j], type: 'rightOnly' });
      j++;
    }
  }
  return result;
}

const EvaluationViewPage: React.FC = () => {
  const [cases, setCases] = useState<EvalCase[]>([]);
  const [logicalCases, setLogicalCases] = useState<LogicalCase[]>([]);
  const [selectedLogical, setSelectedLogical] = useState<LogicalCase | null>(null);
  const [variants, setVariants] = useState<EvalCase[]>([]);
  const [selectedTurnIdx, setSelectedTurnIdx] = useState<number>(0);
  const [showSystemPrompts, setShowSystemPrompts] = useState<boolean>(true);
  const [compareA, setCompareA] = useState<number>(0);
  const [compareB, setCompareB] = useState<number>(1);
  const [showCompareTwo, setShowCompareTwo] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCaseLoading, setIsCaseLoading] = useState(false);
  const [error, setError] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'gold' | 'synthetic'>('all');

  useEffect(() => {
    loadCases();
  }, [sourceFilter]);

  const loadCases = async () => {
    try {
      setIsLoading(true);
      setError('');
      const sourceType = sourceFilter === 'all' ? undefined : sourceFilter;
      const result = await apiService.getEvalCases(sourceType, 1, 500);
      const exp1Only = (result.cases as EvalCase[]).filter(isExp1Case);
      setCases(exp1Only);
      const grouped = groupByLogicalCase(exp1Only);
      setLogicalCases(grouped);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load evaluation cases');
    } finally {
      setIsLoading(false);
    }
  };

  /** Exp1 evaluation: show only first 5 gold + 5 synthetic (sampleId 0..4). */
  const EVAL_GOLD_COUNT = 5;
  const EVAL_SYNTHETIC_COUNT = 5;

  function groupByLogicalCase(caseList: EvalCase[]): LogicalCase[] {
    const map = new Map<string, LogicalCase>();
    caseList.forEach((c) => {
      if (c.sourceType === 'gold' && c.sampleId >= EVAL_GOLD_COUNT) return;
      if (c.sourceType === 'synthetic' && c.sampleId >= EVAL_SYNTHETIC_COUNT) return;
      const key = `${c.sourceType}-${c.sampleId}`;
      if (!map.has(key)) {
        map.set(key, {
          sourceType: c.sourceType as 'gold' | 'synthetic',
          sampleId: c.sampleId,
          title: c.sourceType === 'gold' ? `Gold #${c.sampleId + 1}` : `Synthetic #${c.sampleId + 1}`,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      if (a.sourceType !== b.sourceType) return a.sourceType === 'gold' ? -1 : 1;
      return a.sampleId - b.sampleId;
    });
  }

  const loadLogicalCase = async (logical: LogicalCase) => {
    try {
      setIsCaseLoading(true);
      setSelectedLogical(logical);
      setSelectedTurnIdx(0);
      const { cases: variantCases } = await apiService.getEvalCasesByLogicalCase(logical.sourceType, logical.sampleId);
      const exp1Variants = (variantCases as EvalCase[]).filter(isExp1Case);
      const sorted = [...exp1Variants].sort((a, b) => variantOrder(a.promptVariant ?? '') - variantOrder(b.promptVariant ?? ''));
      setVariants(sorted);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load case');
    } finally {
      setIsCaseLoading(false);
    }
  };

  const numTurns = useMemo(() => {
    if (variants.length === 0) return 0;
    return variants[0].turns?.length ?? 0;
  }, [variants]);

  const selectedTurn = useMemo(() => {
    if (variants.length === 0 || selectedTurnIdx < 0) return null;
    return variants[0].turns?.[selectedTurnIdx] ?? null;
  }, [variants, selectedTurnIdx]);

  /** Build "previous context" for the selected turn from earlier turns (clinician + family) */
  const contextBlocks = useMemo(() => {
    if (variants.length === 0 || selectedTurnIdx <= 0) return [];
    const turns = variants[0].turns ?? [];
    const out: { speaker: string; content: string }[] = [];
    for (let i = 0; i < selectedTurnIdx; i++) {
      const t = turns[i];
      if (t) {
        out.push({ speaker: 'Clinician', content: t.doctorMessage });
        out.push({ speaker: 'Family (expected)', content: t.expectedResponse });
      }
    }
    return out;
  }, [variants, selectedTurnIdx]);

  /** Per-variant, per-model response for the selected turn */
  const variantModelGrid = useMemo(() => {
    const grid: { variant: EvalCase; turn: EvalTurn; medgemma: string; qwen: string }[] = [];
    for (const v of variants) {
      const turn = v.turns?.[selectedTurnIdx];
      if (!turn) continue;
      const medgemma = turn.modelResponses?.find((r: { modelName: string; response: string }) => r.modelName === MEDGEMMA)?.response ?? '—';
      const qwen = turn.modelResponses?.find((r: { modelName: string; response: string }) => r.modelName === QWEN)?.response ?? '—';
      grid.push({ variant: v, turn, medgemma, qwen });
    }
    return grid;
  }, [variants, selectedTurnIdx]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50">
      <div className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-purple-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg shadow-purple-200">
                <SparklesIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  Model Comparison (Exp1)
                </h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  5 gold + 5 synthetic cases · Variants 1–4 (gold) / 1–3 (synthetic) · Turn-by-turn view
                </p>
              </div>
            </div>
            <div className="flex items-center bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setSourceFilter('all')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  sourceFilter === 'all' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setSourceFilter('gold')}
                className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-1.5 ${
                  sourceFilter === 'gold' ? 'bg-white text-yellow-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <StarIconSolid className="h-4 w-4 text-yellow-500" />
                Gold (5)
              </button>
              <button
                onClick={() => setSourceFilter('synthetic')}
                className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-1.5 ${
                  sourceFilter === 'synthetic' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <BeakerIcon className="h-4 w-4 text-blue-500" />
                Synthetic (5)
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Left: 5 gold + 5 synthetic cases */}
          <div className="w-72 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden sticky top-28">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="font-semibold text-gray-800">Cases</h2>
                <p className="text-xs text-gray-500 mt-1">5 gold · 5 synthetic</p>
              </div>
              {isLoading ? (
                <div className="p-8 text-center text-gray-500">Loading...</div>
              ) : logicalCases.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No exp1 cases. Run seed-eval-data-exp1.</div>
              ) : (
                <div className="divide-y divide-gray-50 max-h-[calc(100vh-260px)] overflow-y-auto">
                  {logicalCases.map((logical: LogicalCase) => (
                    <button
                      key={`${logical.sourceType}-${logical.sampleId}`}
                      onClick={() => loadLogicalCase(logical)}
                      className={`w-full text-left px-4 py-3 hover:bg-purple-50 transition-all ${
                        selectedLogical?.sourceType === logical.sourceType && selectedLogical?.sampleId === logical.sampleId
                          ? 'bg-purple-50 border-l-4 border-purple-500'
                          : 'border-l-4 border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {logical.sourceType === 'gold' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">Gold</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Synthetic</span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-900">{logical.title}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0 space-y-4">
            {!selectedLogical ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
                <SparklesIcon className="h-12 w-12 text-purple-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900">Select a case</h3>
                <p className="mt-2 text-gray-500">Choose a case to see variants, turns, and model outputs.</p>
              </div>
            ) : isCaseLoading ? (
              <div className="bg-white rounded-2xl shadow-sm border p-16 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-purple-200 border-t-purple-600 mx-auto" />
                <p className="mt-4 text-gray-500">Loading case...</p>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-4 flex items-center gap-3 flex-wrap">
                    {selectedLogical.sourceType === 'gold' ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">Gold</span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">Synthetic</span>
                    )}
                    <h2 className="text-lg font-bold text-gray-900">{selectedLogical.title}</h2>
                    <span className="text-sm text-gray-500">{variants.length} prompt variants</span>
                  </div>
                </div>

                {/* System prompts: simple "what each variant is" + full prompt, then optional compare two */}
                {variants.length > 0 && selectedLogical && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setShowSystemPrompts(!showSystemPrompts)}
                      className="w-full px-4 py-3 bg-purple-50 border-b border-purple-100 hover:bg-purple-100 transition-colors flex items-center justify-between"
                    >
                      <span className="text-sm font-semibold text-purple-800">What system prompt each variant uses</span>
                      {showSystemPrompts ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                    </button>
                    {showSystemPrompts && (
                      <div className="p-4 space-y-6">
                        <p className="text-sm text-gray-600">
                          Each variant gives the model different instructions. Below: a short summary of what each variant is, then the full text you can expand if needed.
                        </p>

                        {/* One card per variant: title, plain-English description, expandable full prompt */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {variants.map((v) => (
                            <VariantCard
                              key={v.id}
                              variant={v}
                              sourceType={selectedLogical.sourceType}
                            />
                          ))}
                        </div>

                        {/* Optional: compare two variants (plain language) */}
                        <div className="border-t border-gray-200 pt-4">
                          <button
                            onClick={() => setShowCompareTwo(!showCompareTwo)}
                            className="text-sm font-medium text-purple-700 hover:text-purple-900"
                          >
                            {showCompareTwo ? 'Hide' : 'Show'} &quot;Compare two variants&quot; — see exactly which lines appear only in one variant
                          </button>
                          {showCompareTwo && (
                            <div className="mt-4 p-4 bg-gray-50 rounded-xl space-y-4">
                              <p className="text-sm text-gray-700">
                                Pick two variants. We&apos;ll show what text appears only in the first, and only in the second.
                              </p>
                              <div className="flex flex-wrap items-center gap-4">
                                <label className="flex items-center gap-2 text-sm">
                                  <span className="text-gray-600">First variant:</span>
                                  <select
                                    value={compareA}
                                    onChange={(e) => setCompareA(Number(e.target.value))}
                                    className="rounded border border-gray-300 px-2 py-1.5 text-gray-800 bg-white"
                                  >
                                    {variants.map((v: EvalCase, idx: number) => (
                                      <option key={v.id} value={idx}>
                                        {variantShortLabel(v.promptVariant ?? '')}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                  <span className="text-gray-600">Second variant:</span>
                                  <select
                                    value={compareB}
                                    onChange={(e) => setCompareB(Number(e.target.value))}
                                    className="rounded border border-gray-300 px-2 py-1.5 text-gray-800 bg-white"
                                  >
                                    {variants.map((v: EvalCase, idx: number) => (
                                      <option key={v.id} value={idx}>
                                        {variantShortLabel(v.promptVariant ?? '')}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              </div>
                              {compareA !== compareB && (() => {
                                const va = variants[compareA];
                                const vb = variants[compareB];
                                const diffRows = lineDiff(va.systemPrompt, vb.systemPrompt);
                                const nameA = variantShortLabel(va.promptVariant ?? '');
                                const nameB = variantShortLabel(vb.promptVariant ?? '');
                                return (
                                  <div className="max-h-[50vh] min-h-0 overflow-y-auto overflow-x-hidden rounded-lg border border-gray-200 text-xs">
                                    <div className="grid grid-cols-2 gap-0 min-w-0">
                                      <div className="sticky top-0 z-10 px-3 py-2 bg-rose-100 border-b border-r border-gray-200 text-rose-800 font-medium">
                                        Only in {nameA}
                                      </div>
                                      <div className="sticky top-0 z-10 px-3 py-2 bg-emerald-100 border-b border-gray-200 text-emerald-800 font-medium">
                                        Only in {nameB}
                                      </div>
                                      {diffRows.map((row: { left: string; right: string; type: string }, ri: number) => (
                                        <React.Fragment key={ri}>
                                          <div
                                            className={`px-3 py-2 border-b border-r border-gray-100 whitespace-pre-wrap break-words ${
                                              row.type === 'leftOnly' ? 'bg-rose-50 text-rose-900' : row.type === 'common' ? 'bg-gray-50 text-gray-500' : 'bg-white'
                                            }`}
                                          >
                                            {row.left || '\u00a0'}
                                          </div>
                                          <div
                                            className={`px-3 py-2 border-b border-gray-100 whitespace-pre-wrap break-words ${
                                              row.type === 'rightOnly' ? 'bg-emerald-50 text-emerald-900' : row.type === 'common' ? 'bg-gray-50 text-gray-500' : 'bg-white'
                                            }`}
                                          >
                                            {row.right || '\u00a0'}
                                          </div>
                                        </React.Fragment>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}
                              {compareA === compareB && (
                                <p className="text-sm text-amber-700">Pick two different variants to compare.</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Turn selector */}
                {numTurns > 0 && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <h3 className="text-sm font-semibold text-gray-800">Select turn</h3>
                    </div>
                    <div className="p-4 flex flex-wrap gap-2">
                      {Array.from({ length: numTurns }, (_, i) => (
                        <button
                          key={i}
                          onClick={() => setSelectedTurnIdx(i)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            selectedTurnIdx === i
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-purple-50'
                          }`}
                        >
                          Turn {i + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected turn: context, clinician, expected, model×variant grid */}
                {selectedTurn && (
                  <div className="space-y-4">
                    {/* Previous context */}
                    {contextBlocks.length > 0 && (
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-600 uppercase">Previous context (before this turn)</div>
                        <div className="p-4 space-y-2 max-h-48 overflow-y-auto">
                          {contextBlocks.map((b: { speaker: string; content: string }, i: number) => (
                            <div key={i} className="text-sm">
                              <span className={`font-medium ${b.speaker === 'Clinician' ? 'text-blue-600' : 'text-emerald-600'}`}>{b.speaker}:</span>{' '}
                              <span className="text-gray-700">{b.content}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Clinician (this turn) + Expected response — single blocks */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                      <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 text-xs font-semibold text-blue-800 uppercase">Clinician (this turn)</div>
                      <div className="p-4">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{selectedTurn.doctorMessage}</p>
                      </div>
                    </div>
                    {selectedLogical.sourceType === 'gold' && (
                      <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 overflow-hidden">
                        <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-100 text-xs font-semibold text-emerald-800 uppercase">Expected response (ground truth)</div>
                        <div className="p-4">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedTurn.expectedResponse}</p>
                        </div>
                      </div>
                    )}

                    {/* Model × Variant grid: rows = variants, columns = MedGemma | Qwen */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="grid grid-cols-2 gap-0 border-b border-gray-200 bg-gray-50">
                        <div className={`px-4 py-3 text-sm font-semibold uppercase ${getModelColor(MEDGEMMA).text} border-r border-gray-200`}>MedGemma 4B Patient LoRA</div>
                        <div className={`px-4 py-3 text-sm font-semibold uppercase ${getModelColor(QWEN).text}`}>Qwen 2.5-7B Patient LoRA</div>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {variantModelGrid.map(({ variant, medgemma, qwen }: { variant: EvalCase; medgemma: string; qwen: string }) => (
                          <div key={variant.id} className="grid grid-cols-2 gap-0 min-h-[2rem]">
                            <div className={`px-4 py-3 border-r border-gray-100 ${getModelColor(MEDGEMMA).bg}`}>
                              <p className="text-xs font-medium text-purple-600 mb-1">{variantShortLabel(variant.promptVariant ?? '')}</p>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">{medgemma}</p>
                            </div>
                            <div className={`px-4 py-3 ${getModelColor(QWEN).bg}`}>
                              <p className="text-xs font-medium text-rose-600 mb-1">{variantShortLabel(variant.promptVariant ?? '')}</p>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">{qwen}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
                        Turn {selectedTurnIdx + 1} · Same clinician line{selectedLogical.sourceType === 'gold' ? ' and expected response' : ''}; model outputs vary by system-prompt variant.
                      </div>
                    </div>
                  </div>
                )}

                {selectedLogical && variants.length === 0 && (
                  <p className="text-gray-500 text-sm">No variant data for this case. Ensure exp1 seed has been run.</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/** One card per variant: title, plain-English "what this variant is", then expandable full prompt */
function VariantCard({ variant, sourceType }: { variant: EvalCase; sourceType: 'gold' | 'synthetic' }) {
  const [showFull, setShowFull] = useState(false);
  const label = variantShortLabel(variant.promptVariant ?? '');
  const description = variantDescription(variant.promptVariant ?? '', sourceType);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <div className="px-4 py-3 bg-purple-50 border-b border-purple-100">
        <h4 className="font-semibold text-purple-900">{label}</h4>
      </div>
      <div className="p-4">
        <p className="text-sm text-gray-700 mb-3">
          <span className="font-medium text-gray-800">What this variant is: </span>
          {description}
        </p>
        <button
          onClick={() => setShowFull(!showFull)}
          className="text-sm text-purple-600 hover:text-purple-800 font-medium"
        >
          {showFull ? 'Hide full prompt' : 'Show full prompt'}
        </button>
        {showFull && (
          <pre className="mt-3 p-3 bg-gray-50 text-xs text-gray-700 whitespace-pre-wrap rounded-lg border border-gray-100 max-h-64 overflow-y-auto">
            {variant.systemPrompt}
          </pre>
        )}
      </div>
    </div>
  );
}

export default EvaluationViewPage;
